/**
 * Stage: Conversational Editing
 * NL-based transcript editing via chat interface.
 * Replaces SUBTITLE_EDITING for conversational_mode drafts.
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseDraft } from '../../../types/creator-workflow';
import { creatorWorkflowService } from '../../../services/creatorWorkflowService';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import WaveformPlayer, { WaveformPlayerHandle } from '../../subtitle-editor/WaveformPlayer';
import ConversationalEditor from '../../conversational-editor/ConversationalEditor';
import './StageConversationalEditing.css';

const formatTime = (seconds: number): string => {
    if (!seconds || !isFinite(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

interface StageConversationalEditingProps {
    draft: CourseDraft;
    onAdvanceStage: () => Promise<void>;
    onGoBackStage: () => Promise<void>;
    onDraftUpdate?: (draft: CourseDraft) => void;
    onParseContent: () => Promise<void>;
    loading: boolean;
}

const StageConversationalEditing: React.FC<StageConversationalEditingProps> = ({
    draft,
    onParseContent,
    loading,
}) => {
    const { t } = useTranslation(['conversationalEditor', 'creator', 'common']);
    const [sentences, setSentences] = useState<any[]>(draft.sentences || []);
    const [advancing, setAdvancing] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playingSentenceIndex, setPlayingSentenceIndex] = useState<number | null>(null);
    const waveformRef = useRef<WaveformPlayerHandle>(null);
    const sentenceStopTimerRef = useRef<number | null>(null);

    // Debounced PATCH for sentence edits — without this, dragging a region
    // on the waveform only updates local state, so any republish or unmount
    // reads stale data from the server. handleDone (advancing stage) saved
    // explicitly, but the "Republish" CTA on the wizard header doesn't go
    // through handleDone, so unsaved drag changes were lost.
    //
    // 500ms debounce = wait until the creator stops adjusting before paying
    // the round-trip; the auto-save indicator in the wizard header surfaces
    // the in-flight save so they don't navigate away mid-write.
    const initialSentencesRef = useRef(draft.sentences || []);
    const saveTimerRef = useRef<number | null>(null);
    useEffect(() => {
        // Skip the initial "settle" pass where sentences === draft.sentences
        if (sentences === initialSentencesRef.current) return;
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = window.setTimeout(() => {
            creatorWorkflowService.updateDraft(draft.id, {
                sentences,
                generated_text: sentences.map((s: any) => s.text).join('\n'),
            }).catch((err) => {
                console.error('Auto-save failed:', err);
            });
            saveTimerRef.current = null;
        }, 500);
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
        };
    }, [sentences, draft.id]);

    // Resolve audio URL for playback (trimmed > original)
    const audioUrl = useMemo(() => {
        const progress = draft.stage_progress?.audio_generation;
        const raw = draft.subtitle_editing_audio_url
            || draft.uploaded_audio_url
            || progress?.merged_audio_url
            || null;
        return raw ? resolveMediaUrl(raw) : null;
    }, [draft]);

    // Convert sentences to WaveformPlayer subtitle format
    const subtitles = useMemo(() =>
        sentences.map((s: any, i: number) => ({
            index: i,
            text: s.text || '',
            startTime: s.start_time ?? 0,
            endTime: s.end_time ?? 0,
        })),
    [sentences]);

    const handleSentencesChange = useCallback((updated: any[]) => {
        setSentences(updated);
    }, []);

    // When a region is dragged in the waveform, update sentence timing
    const handleRegionUpdate = useCallback((index: number, start: number, end: number) => {
        setSentences(prev => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index] = {
                    ...updated[index],
                    start_time: start,
                    end_time: end,
                    duration: end - start,
                };
            }
            return updated;
        });
    }, []);

    const handleRegionClick = useCallback((index: number) => {
        setSelectedIndex(index);
    }, []);

    const handleTimeUpdate = useCallback((time: number) => {
        setCurrentTime(time);
    }, []);

    const handlePlayStateChange = useCallback((playing: boolean) => {
        setIsPlaying(playing);
    }, []);

    const handleReady = useCallback(() => {
        if (waveformRef.current) {
            setDuration(waveformRef.current.getDuration());
        }
    }, []);

    const handlePlayPause = useCallback(() => {
        if (!waveformRef.current) return;
        if (waveformRef.current.isPlaying()) {
            waveformRef.current.pause();
        } else {
            waveformRef.current.play();
        }
    }, []);

    // Play a specific sentence via the waveform player
    const handlePlaySentence = useCallback((index: number) => {
        const s = sentences[index];
        if (!waveformRef.current || s?.start_time == null) return;

        // Clear previous stop timer
        if (sentenceStopTimerRef.current) {
            clearTimeout(sentenceStopTimerRef.current);
            sentenceStopTimerRef.current = null;
        }

        setPlayingSentenceIndex(index);
        setSelectedIndex(index);
        waveformRef.current.playSegment(s.start_time, s.end_time ?? s.start_time + 5);

        // Auto-stop & clear playing state when segment ends
        if (s.end_time != null) {
            const durationMs = (s.end_time - s.start_time) * 1000 + 100; // small buffer
            sentenceStopTimerRef.current = window.setTimeout(() => {
                setPlayingSentenceIndex(null);
                sentenceStopTimerRef.current = null;
            }, durationMs);
        }
    }, [sentences]);

    const handleStopSentence = useCallback(() => {
        if (sentenceStopTimerRef.current) {
            clearTimeout(sentenceStopTimerRef.current);
            sentenceStopTimerRef.current = null;
        }
        waveformRef.current?.pause();
        setPlayingSentenceIndex(null);
    }, []);

    const handleDone = useCallback(async () => {
        if (advancing) return;
        setAdvancing(true);

        try {
            // Save final sentences to draft
            await creatorWorkflowService.updateDraft(draft.id, {
                sentences,
                generated_text: sentences.map((s: any) => s.text).join('\n'),
            });

            // Kick off parsing — backend's parseContent() transitions the stage to
            // PARSING and starts the background parser, which sets parsing_status
            // to 'in_progress' so Stage2Parsing's progress UI actually renders.
            // (Calling onAdvanceStage alone would move to PARSING with no parsing
            // started, leaving the page blank.)
            await onParseContent();
        } catch (error: any) {
            console.error('Failed to advance from conversational editing:', error);
            alert(error.response?.data?.message || error.message);
        } finally {
            setAdvancing(false);
        }
    }, [draft.id, sentences, onParseContent, advancing]);

    return (
        <div className="stage-conversational-editing">
            {/* Waveform player with controls + sentence regions */}
            {audioUrl && (
                <div className="ce-waveform-container">
                    <div className="ce-player-controls">
                        <button
                            className="ce-ctrl-btn ce-ctrl-btn--play"
                            onClick={handlePlayPause}
                        >
                            {isPlaying ? (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            ) : (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                        <div className="ce-ctrl-time">
                            {formatTime(currentTime)}
                            <span className="ce-ctrl-time__sep">/</span>
                            {formatTime(duration)}
                        </div>
                    </div>
                    <div className="ce-waveform-area">
                        <WaveformPlayer
                            ref={waveformRef}
                            audioUrl={audioUrl}
                            subtitles={subtitles}
                            selectedIndex={selectedIndex}
                            onRegionClick={handleRegionClick}
                            onRegionUpdate={handleRegionUpdate}
                            onTimeUpdate={handleTimeUpdate}
                            onPlayStateChange={handlePlayStateChange}
                            onReady={handleReady}
                        />
                    </div>
                </div>
            )}

            <ConversationalEditor
                draftId={draft.id}
                sentences={sentences}
                onSentencesChange={handleSentencesChange}
                onDone={handleDone}
                audioUrl={audioUrl}
                focusedIndex={selectedIndex}
                onPlaySentence={handlePlaySentence}
                onStopSentence={handleStopSentence}
                playingSentenceIndex={playingSentenceIndex}
            />

            {(loading || advancing) && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>{t('common:processing')}</p>
                </div>
            )}
        </div>
    );
};

export default StageConversationalEditing;
